/**
 * Auth API Queries
 * React Query hooks for identity, tenant, and user management
 *
 * Gateway mapping: /auth/* → ai-auth service
 */

import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { apiGet } from '@/api/_shared/http';
import { STALE_2M, STALE_5M, RETRY_ONCE, STALE_30S } from '@/api/_shared/queryDefaults';
import { authKeys } from './keys';
import type {
  ProfileResponse,
  TenantMembershipsResponse,
  User,
  ClientsWithCompaniesResponse,
  ClientsListQueryParams,
  UsersWithTenantsResponse,
  ClientResponse,
  CompanyResponse,
  ClientUsersResponse,
  UserTenantsByUserIdResponse,
  WeeklySummarySettingsResponse
} from './interfaces';


import { AUTH_BASE } from './paths';
/** Auth service routes used here that remain ADMIN-only (DEVELOPER blocked): GET /users, GET /company/:id; PUT /users/:id/role (mutations). */

// ============================================================================
// QUERY FUNCTIONS (Reusable API calls)
// ============================================================================

/**
 * Fetches the current user's profile information.
 *
 * @returns Profile payload for the authenticated user
 */
export const getProfileQueryFn = async (): Promise<ProfileResponse> =>
  apiGet<ProfileResponse>(`${AUTH_BASE}/profile`);

/**
 * Fetches the current user's tenant memberships (clients and companies).
 *
 * @returns Tenant membership list for the authenticated user
 */
export const getTenantMembershipsQueryFn = async (): Promise<TenantMembershipsResponse> =>
  apiGet<TenantMembershipsResponse>(`${AUTH_BASE}/tenants/memberships`);

/**
 * Fetches all users (Admin only).
 *
 * @returns All platform users
 */
export const getUsersListQueryFn = async (): Promise<User[]> => apiGet<User[]>(`${AUTH_BASE}/users`);

/**
 * Fetches all clients with their associated companies (Admin only, paginated).
 *
 * @param params - Pagination and sort filters
 * @returns Paginated clients with nested companies
 */
export const getClientsWithCompaniesQueryFn = async (
  params: ClientsListQueryParams
): Promise<ClientsWithCompaniesResponse> =>
  apiGet<ClientsWithCompaniesResponse>(`${AUTH_BASE}/clients`, {
    params: {
      page: params.page,
      limit: params.limit,
      ...(params.search?.trim() && { search: params.search.trim() }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortOrder && { sortOrder: params.sortOrder })
    }
  });

/**
 * Fetches all users with their tenant memberships (Admin only, paginated).
 *
 * @param page - Page number
 * @param limit - Page size
 * @param companyId - Optional company filter
 * @param clientId - Optional client filter
 * @param role - Optional role filter
 * @param search - Optional search string
 * @returns Paginated users with tenant memberships
 */
export const getUsersWithTenantsQueryFn = async (
  page: number,
  limit: number,
  companyId?: string,
  clientId?: string,
  role?: string,
  search?: string
): Promise<UsersWithTenantsResponse> => {
  const params: {
    page: number;
    limit: number;
    companyId?: string;
    clientId?: string;
    role?: string;
    search?: string;
  } = { page, limit };
  if (companyId) params.companyId = companyId;
  if (clientId) params.clientId = clientId;
  if (role) params.role = role;
  if (search?.trim()) params.search = search.trim();

  return apiGet<UsersWithTenantsResponse>(`${AUTH_BASE}/users/tenants`, { params });
};

/**
 * Fetches a specific client by ID with associated companies.
 *
 * @param clientId - Client ID to fetch
 * @returns Client with companies
 */
export const getClientByIdQueryFn = async (clientId: string): Promise<ClientResponse> =>
  apiGet<ClientResponse>(`${AUTH_BASE}/client/${clientId}`);

/**
 * Fetches a specific company by ID with client information (Admin only).
 *
 * @param companyId - Company ID to fetch
 * @returns Company with parent client
 */
export const getCompanyByIdQueryFn = async (companyId: string): Promise<CompanyResponse> =>
  apiGet<CompanyResponse>(`${AUTH_BASE}/company/${companyId}`);

/**
 * Fetches a specific user's tenant memberships by user ID.
 *
 * @param userId - User ID to fetch memberships for
 * @returns User tenant memberships
 */
export const getUserTenantsByUserIdQueryFn = async (userId: string): Promise<UserTenantsByUserIdResponse> =>
  apiGet<UserTenantsByUserIdResponse>(`${AUTH_BASE}/user/tenants/${userId}`);

/**
 * Fetches employees of a client.
 *
 * @param clientId - Client ID
 * @returns Client employees
 */
export const getClientEmployeesQueryFn = async (clientId: string): Promise<ClientUsersResponse> =>
  apiGet<ClientUsersResponse>(`${AUTH_BASE}/client/${clientId}/employees`);

/**
 * Fetches owners of a client.
 *
 * @param clientId - Client ID
 * @returns Client owners
 */
export const getClientOwnersQueryFn = async (clientId: string): Promise<ClientUsersResponse> =>
  apiGet<ClientUsersResponse>(`${AUTH_BASE}/client/${clientId}/owners`);

/**
 * Fetches customers of a client (ADMIN/DEVELOPER or client OWNER/EMPLOYEE).
 *
 * @param clientId - Client ID
 * @returns Client customers
 */
export const getClientCustomersQueryFn = async (clientId: string): Promise<ClientUsersResponse> =>
  apiGet<ClientUsersResponse>(`${AUTH_BASE}/client/${clientId}/customers`);

export const getWeeklySummarySettingsQueryFn = async (clientId: string): Promise<WeeklySummarySettingsResponse> =>
  apiGet<WeeklySummarySettingsResponse>(`${AUTH_BASE}/client/${clientId}/weekly-summary-settings`);

// ============================================================================
// REACT QUERY HOOKS
// ============================================================================

/**
 * Hook to fetch the current user's profile information.
 *
 * @param enabled - Whether the query should be enabled
 */
export const useProfileQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.profile(),
    queryFn: getProfileQueryFn,
    enabled,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch the current user's tenant memberships.
 *
 * @param enabled - Whether the query should be enabled
 */
export const useTenantMembershipsQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.tenantMemberships(),
    queryFn: getTenantMembershipsQueryFn,
    enabled,
    staleTime: STALE_2M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch all users (Admin only).
 *
 * @param enabled - Whether the query should be enabled
 */
export const useUsersListQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.usersList(),
    queryFn: getUsersListQueryFn,
    enabled,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch all clients with their associated companies (Admin only, paginated).
 *
 * @param params - Pagination and sort filters
 * @param enabled - Whether the query should be enabled
 */
export const useClientsWithCompaniesQuery = (params: ClientsListQueryParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.clientsWithCompanies(params),
    queryFn: () => getClientsWithCompaniesQueryFn(params),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: STALE_30S,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch all users with their tenant memberships (Admin only, paginated).
 *
 * @param page - Page number
 * @param limit - Page size
 * @param companyId - Optional company filter
 * @param clientId - Optional client filter
 * @param role - Optional role filter
 * @param search - Optional search string
 * @param enabled - Whether the query should be enabled
 */
export const useUsersWithTenantsQuery = (
  page: number = 1,
  limit: number = 10,
  companyId?: string,
  clientId?: string,
  role?: string,
  search?: string,
  enabled: boolean = true
) => {
  const filters = { page, limit, companyId, clientId, role, search };
  return useQuery({
    queryKey: authKeys.usersWithTenants(filters),
    queryFn: () => getUsersWithTenantsQueryFn(page, limit, companyId, clientId, role, search),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch a specific client by ID with associated companies.
 *
 * @param clientId - Client ID to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useClientByIdQuery = (clientId: string, enabled: boolean = true) => {
  return useQuery({
    ...clientByIdQueryOptions(clientId),
    enabled: enabled && !!clientId
  });
};

/**
 * Query options for one client by ID; shared by {@link useClientByIdQuery} and route loaders
 * that prefetch the client before rendering.
 */
export const clientByIdQueryOptions = (clientId: string) =>
  queryOptions({
    queryKey: authKeys.client(clientId),
    queryFn: () => getClientByIdQueryFn(clientId),
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });

/**
 * Hook to fetch a specific company by ID with client information (Admin only).
 *
 * @param companyId - Company ID to fetch
 * @param enabled - Whether the query should be enabled
 */
export const useCompanyByIdQuery = (companyId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.company(companyId),
    queryFn: () => getCompanyByIdQueryFn(companyId),
    enabled: enabled && !!companyId,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch a specific user's tenant memberships by user ID (Admin only).
 *
 * @param userId - User ID to fetch memberships for
 * @param enabled - Whether the query should be enabled
 */
export const useUserTenantsByUserIdQuery = (userId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.userTenantsByUserId(userId),
    queryFn: () => getUserTenantsByUserIdQueryFn(userId),
    enabled: enabled && !!userId,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch employees of a client (accessible by admins and owners).
 *
 * @param clientId - Client ID
 * @param enabled - Whether the query should be enabled
 */
export const useClientEmployeesQuery = (clientId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.clientEmployees(clientId),
    queryFn: () => getClientEmployeesQueryFn(clientId),
    enabled: enabled && !!clientId,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch owners of a client (global ADMIN or DEVELOPER).
 *
 * @param clientId - Client ID
 * @param enabled - Whether the query should be enabled
 */
export const useClientOwnersQuery = (clientId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.clientOwners(clientId),
    queryFn: () => getClientOwnersQueryFn(clientId),
    enabled: enabled && !!clientId,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

/**
 * Hook to fetch customers of a client (ADMIN/DEVELOPER or client OWNER/EMPLOYEE).
 *
 * @param clientId - Client ID
 * @param enabled - Whether the query should be enabled
 */
export const useClientCustomersQuery = (clientId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.clientCustomers(clientId),
    queryFn: () => getClientCustomersQueryFn(clientId),
    enabled: enabled && !!clientId,
    staleTime: STALE_5M,
    retry: RETRY_ONCE
  });
};

export const useWeeklySummarySettingsQuery = (clientId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.weeklySummarySettings(clientId),
    queryFn: () => getWeeklySummarySettingsQueryFn(clientId),
    enabled: enabled && !!clientId,
    staleTime: STALE_2M,
    retry: RETRY_ONCE
  });
};
